import { createApp } from 'vue';
import { createPinia } from 'pinia';
import router from './router';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

import './assets/main.css';

import { resolveRuntimeEnv } from '@sera/core';
import App from './app/App.vue';
import { addVueError } from '@datadog/browser-rum-vue';
import { initSentry } from './plugins/sentry';
import { initDatadogRum } from './plugins/datadog';
import { installPrimeVue } from './plugins/primevue';
import { setupFontAwesome, FontAwesomeIcon } from './plugins/fontawesome';

const app = createApp(App);
const pinia = createPinia();

// 1. Observability first (before app.use() calls). Both no-op in local dev.
//    Sentry = errors/replay; Datadog RUM = views/resources/web-vitals/actions.
//    initDatadogRum() must run before app.use(router) below so RUM is live when
//    the router's first navigation fires its view. addVueError reports Vue
//    errors to RUM; setting it BEFORE initSentry lets Sentry's handler wrap and
//    chain to it (Sentry captures the prior errorHandler and calls it), so both
//    RUM and Sentry receive every Vue error with component stack traces.
initDatadogRum();
app.config.errorHandler = addVueError;
initSentry(app, router);

// 2. State and routing
app.use(pinia);
app.use(router);

// 3. UI primitives
installPrimeVue(app);
app.use(ToastService);
app.directive('tooltip', Tooltip);

// 4. Font Awesome
setupFontAwesome();
app.component('FontAwesomeIcon', FontAwesomeIcon);

// 5. Mount — tag the DOM root with the runtime environment (derived from the
//    hostname, same source as Sentry's environment tag) so Playwright/Sentry can
//    confirm which env is serving. NOT VITE_ENV: the app builds once with no
//    per-env target, so VITE_ENV is empty in every deploy.
const mountEl = document.getElementById('app');
if (mountEl) {
  mountEl.dataset.env = resolveRuntimeEnv();
  app.mount(mountEl); // pass the element directly; avoids a second querySelector
} else {
  console.error('[portal] Mount target #app not found — app not started');
}
